import React from 'react';
import { cx } from '../cx';

// D-09 §15.39 — 이미지 선택 그리드 (Photo Select Grid) · controlled 다중선택
// 표현 전용: 데이터 조회·필터는 소비처가 소유하고, 여기엔 items/선택상태/토글만 넘긴다.
export type PhotoId = number | string;

export interface PhotoGridItem {
  id: PhotoId;
  url: string;
  alt?: string;
}

export interface PhotoGridProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onToggle'> {
  items: PhotoGridItem[];
  /** 선택된 id 목록 (controlled) */
  selectedIds: PhotoId[];
  onToggle: (id: PhotoId, item: PhotoGridItem) => void;
  /** 그리드 최소 열 너비(px). 기본 120 */
  minColWidth?: number;
  maxHeight?: number;
  loading?: boolean;
  loadingText?: string;
  emptyText?: string;
  /** 썸네일 렌더 커스텀(예: 인증 blob 로더). 미지정 시 <img src={url}> */
  renderThumb?: (item: PhotoGridItem) => React.ReactNode;
}

export function PhotoGrid({
  items,
  selectedIds,
  onToggle,
  minColWidth = 120,
  maxHeight,
  loading = false,
  loadingText = '불러오는 중…',
  emptyText = '해당 조건의 사진이 없습니다.',
  renderThumb,
  className = '',
  style,
  ...props
}: PhotoGridProps) {
  if (loading) return <p className="photo-grid-state">{loadingText}</p>;
  if (items.length === 0) return <p className="photo-grid-state">{emptyText}</p>;

  const selected = new Set(selectedIds);
  return (
    <div
      className={cx('photo-grid', className)}
      style={{ ['--photo-grid-min' as string]: `${minColWidth}px`, maxHeight, ...style }}
      {...props}
    >
      {items.map((p) => {
        const on = selected.has(p.id);
        return (
          <button
            key={p.id}
            type="button"
            className={cx('photo-grid-item', on && 'selected')}
            aria-pressed={on}
            onClick={() => onToggle(p.id, p)}
          >
            {renderThumb ? renderThumb(p) : <img src={p.url} alt={p.alt ?? ''} loading="lazy" />}
            {on && <span className="photo-grid-check">✓</span>}
          </button>
        );
      })}
    </div>
  );
}

export interface PhotoStripProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  items: PhotoGridItem[];
  onRemove: (id: PhotoId, item: PhotoGridItem) => void;
  /** 스트립 상단 제목(예: "선택된 사진 3장"). 생략 시 제목 줄 없음 */
  title?: React.ReactNode;
  /** 썸네일 렌더 커스텀(예: 인증 blob 로더). 미지정 시 <img src={url}> */
  renderThumb?: (item: PhotoGridItem) => React.ReactNode;
}

/** 선택된 사진 스트립 — 썸네일 + × 제거. 그리드와 분리, 위치는 소비처가 정한다. */
export function PhotoStrip({ items, onRemove, title, renderThumb, className = '', ...props }: PhotoStripProps) {
  if (items.length === 0) return null;
  return (
    <div className={cx('photo-strip', className)} {...props}>
      {title != null && <div className="photo-strip-title">{title}</div>}
      <div className="photo-strip-list">
        {items.map((p) => (
          <div key={p.id} className="photo-strip-thumb">
            {renderThumb ? renderThumb(p) : <img src={p.url} alt={p.alt ?? ''} />}
            <button
              type="button"
              className="photo-strip-remove"
              aria-label="제거"
              onClick={() => onRemove(p.id, p)}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
